import { call, put, takeEvery, all, fork } from "redux-saga/effects";
import { api } from "../services/apiService";
import type from "../actions/types";
import { isApiFailure, isUpdateApiFailure, apiFailureStatus } from "./apiFailure";
import { trackAccountCreated } from "../utils/account-created";

function* addUser(action) {
    try {
        const userRes = yield call(api.addUserApi, action.payload);
        if (userRes.errStatus) {
            yield put({ type: type.ADD_USER_ERROR, status: userRes.errStatus });
        } else {
            yield put({ type: type.ADD_USER_SUCCESS, addUser: userRes });
        }
    } catch (e) {
        yield put({ type: type.ADD_USER_ERROR, message: e.message });
    }
}

function* loginUser(action) {
    try {
        const userRes = yield call(api.doLogin, action.payload);
        if (userRes.errStatus) {
            yield put({ type: type.LOGIN_USER_ERROR, status: userRes.errStatus, errMsg: userRes.errMsg });
        } else {
            yield put({ type: type.LOGIN_USER_SUCCESS, user: userRes });
        }
    } catch (e) {
        yield put({ type: type.LOGIN_USER_ERROR, message: e.message });
    }
}

function* userDetails(action) {
    try {
        const userDetailsRes = yield call(api.getUserDetails, action.payload);
        if (isApiFailure(userDetailsRes)) {
            yield put({
                type: type.USER_DETAILS_ERROR,
                status: apiFailureStatus(userDetailsRes),
            });
        } else {
            yield put({
                type: type.USER_DETAILS_SUCCESS,
                userDetails: userDetailsRes,
            });
        }
    } catch (e) {
        yield put({ type: type.USER_DETAILS_ERROR, message: e.message });
    }
}

function* userDetailsFromToken(action) {
    try {
        const userDetailsRes = yield call(api.getUserDetailsFromToken, action.payload);
        if (isApiFailure(userDetailsRes)) {
            yield put({
                type: type.USER_DETAILS_FROM_TOKEN_ERROR,
                status: apiFailureStatus(userDetailsRes),
                requestId: action.requestId,
            });
        } else {
            // Before the page hears of the user and navigates. Never throws.
            trackAccountCreated(userDetailsRes, { door: action.door });
            yield put({
                type: type.USER_DETAILS_FROM_TOKEN_SUCCESS,
                userDetailsFromToken: userDetailsRes,
                requestId: action.requestId,
            });
        }
    } catch (e) {
        yield put({ type: type.USER_DETAILS_FROM_TOKEN_ERROR, message: e.message, requestId: action.requestId });
    }
}

function* getUserActivity(action) {
    try {
        const userActivityRes = yield call(api.getUserActivity, action.payload);
        // getUserActivity is doGetCall-backed and had NO guard: any failure
        // resolve ({status, errMsg}) fell straight into SUCCESS (#603 gate
        // finding 3). Classify before dispatching.
        if (isApiFailure(userActivityRes)) {
            yield put({
                type: type.GET_USER_ACTIVITY_ERROR,
                status: apiFailureStatus(userActivityRes),
            });
        } else {
            yield put({
                type: type.GET_USER_ACTIVITY_SUCCESS,
                userActivity: userActivityRes,
            });
        }
    } catch (e) {
        yield put({ type: type.GET_USER_ACTIVITY_ERROR, message: e.message });
    }
}

function* setUserActivity(action) {
    try {
        const userActivityRes = yield call(api.setUserActivity, action.payload);
        yield put({
            type: type.SET_USER_ACTIVITY_SUCCESS,
            userActivity: userActivityRes,
        });
    } catch (e) {
        yield put({ type: type.SET_USER_ACTIVITY_ERROR, message: e.message });
    }
}

function* getUserPreference(action) {
    try {
        const userPreferenceRes = yield call(api.getPreference, action.payload);
        // doGetCall NEVER rejects: an HTTP failure resolves {status, errMsg}
        // and a network failure resolves the Error itself — classify here or
        // every failure parades as SUCCESS and the ERROR branch is dead code
        // (QUI#540 re-gate finding N1: a failed GET then enabled the goal
        // editor over an empty map).
        if (isApiFailure(userPreferenceRes)) {
            yield put({
                type: type.GET_PREFERENCE_ERROR,
                status: userPreferenceRes?.status,
                message: userPreferenceRes?.errMsg || userPreferenceRes?.message
            });
            return;
        }
        yield put({
            type: type.GET_PREFERENCE_SUCCESS,
            preference: userPreferenceRes
        });
    } catch (e) {
        yield put({ type: type.GET_PREFERENCE_ERROR, message: e.message });
    }
}

function* setUserPreference(action) {
    let succeeded = false;
    try {
        const userPreferenceRes = yield call(api.setPreference, action.payload);
        // doUpdateCall resolves {errStatus, errMsg} on HTTP failure and the
        // Error on network failure (same never-reject contract) — a failed
        // save must land in ERROR, not overwrite state as a "saved"
        // preference (QUI#540 re-gate finding N3). Update-family predicate:
        // errStatus only (doUpdateCall never emits `status`; #612 gate).
        if (isUpdateApiFailure(userPreferenceRes)) {
            yield put({
                type: type.SET_PREFERENCE_ERROR,
                status: userPreferenceRes?.errStatus,
                message: userPreferenceRes?.errMsg || userPreferenceRes?.message
            });
            return;
        }
        yield put({
            type: type.SET_PREFERENCE_SUCCESS,
            preference: userPreferenceRes
        });
        succeeded = true;
    } catch (e) {
        yield put({ type: type.SET_PREFERENCE_ERROR, message: e.message });
    } finally {
        // Optional request-local acknowledgement; never part of the API payload.
        if (typeof action.meta?.onSettled === "function") {
            try {
                yield call(action.meta.onSettled, succeeded);
            } catch (_) {
                // A request-local UI observer must not terminate global save watchers.
                console.warn("Preference save observer failed");
            }
        }
    }
}

// Test seam: the preference sagas' error CLASSIFICATION is behavior worth
// pinning (the api layer never rejects — see user.preference.test.js).
export const testable = { getUserPreference, setUserPreference, userDetails, userDetailsFromToken, getUserActivity, setUserActivity };

function* setFeedback(action) {
    try {
        const userFeedback = yield call(api.setFeedback, action.payload);
        if (!userFeedback.errStatus) {
            yield put({
                type: type.SET_FEEDBACK_SUCCESS,
                feedback: userFeedback
            });
        } else {
            yield put({ type: type.SET_FEEDBACK_ERROR });
        }
    } catch (e) {
        yield put({ type: type.SET_FEEDBACK_ERROR, message: e.message });
    }
}

function* watchAddUser() {
    yield takeEvery(type.ADD_USER_REQUEST, addUser);
}

function* watchLogin() {
    yield takeEvery(type.LOGIN_USER_REQUEST, loginUser);
}

function* watchUserDetails() {
    yield takeEvery(type.USER_DETAILS_REQUEST, userDetails);
}

function* watchUserDetailsFromToken() {
    yield takeEvery(type.USER_DETAILS_FROM_TOKEN_REQUEST, userDetailsFromToken);
}

function* watchGetUserActivity() {
    yield takeEvery(type.GET_USER_ACTIVITY_REQUEST, getUserActivity);
}

function* watchSetUserActivity() {
    yield takeEvery(type.SET_USER_ACTIVITY_REQUEST, setUserActivity);
}

function* watchGetUserPreference() {
    yield takeEvery(type.GET_PREFERENCE_REQUEST, getUserPreference);
}

function* watchSetUserPreference() {
    yield takeEvery(type.SET_PREFERENCE_REQUEST, setUserPreference);
}

function* watchSetFeedback() {
    yield takeEvery(type.SET_FEEDBACK_REQUEST, setFeedback);
}

function* sendOTPSaga(action) {
    try {
        const response = yield call(api.sendOTP, action.payload);
        if (response.errStatus) {
            yield put({
                type: type.SEND_OTP_ERROR,
                status: response.errStatus,
                errMsg: response.errMsg
            });
        } else {
            yield put({
                type: type.SEND_OTP_SUCCESS,
                payload: response
            });
        }
    } catch (e) {
        yield put({
            type: type.SEND_OTP_ERROR,
            status: 500,
            errMsg: e.message
        });
    }
}

function* verifyOTPSaga(action) {
    try {
        const response = yield call(api.verifyOTP, action.payload);
        if (response.errStatus) {
            yield put({
                type: type.VERIFY_OTP_ERROR,
                status: response.errStatus,
                errMsg: response.errMsg
            });
        } else {
            yield put({
                type: type.VERIFY_OTP_SUCCESS,
                payload: response
            });
        }
    } catch (e) {
        yield put({
            type: type.VERIFY_OTP_ERROR,
            status: 500,
            errMsg: e.message
        });
    }
}

function* completeProfileSaga(action) {
    try {
        const response = yield call(api.completeProfile, action.payload);
        if (response.errStatus) {
            yield put({
                type: type.COMPLETE_PROFILE_ERROR,
                status: response.errStatus,
                errMsg: response.errMsg
            });
        } else {
            yield put({
                type: type.COMPLETE_PROFILE_SUCCESS,
                payload: response
            });
        }
    } catch (e) {
        yield put({
            type: type.COMPLETE_PROFILE_ERROR,
            status: 500,
            errMsg: e.message
        });
    }
}

function* sendEmailOTPSaga(action) {
    try {
        const response = yield call(api.sendEmailOTP, action.payload);
        if (response.errStatus) {
            yield put({
                type: type.SEND_EMAIL_OTP_ERROR,
                status: response.errStatus,
                errMsg: response.errMsg
            });
        } else {
            yield put({
                type: type.SEND_EMAIL_OTP_SUCCESS,
                payload: response
            });
        }
    } catch (e) {
        yield put({
            type: type.SEND_EMAIL_OTP_ERROR,
            status: 500,
            errMsg: e.message
        });
    }
}

function* verifyEmailOTPSaga(action) {
    try {
        const response = yield call(api.verifyEmailOTP, action.payload);
        if (response.errStatus) {
            yield put({
                type: type.VERIFY_EMAIL_OTP_ERROR,
                status: response.errStatus,
                errMsg: response.errMsg
            });
        } else {
            yield put({
                type: type.VERIFY_EMAIL_OTP_SUCCESS,
                payload: response
            });
        }
    } catch (e) {
        yield put({
            type: type.VERIFY_EMAIL_OTP_ERROR,
            status: 500,
            errMsg: e.message
        });
    }
}

function* watchSendOTP() {
    yield takeEvery(type.SEND_OTP_REQUEST, sendOTPSaga);
}

function* watchVerifyOTP() {
    yield takeEvery(type.VERIFY_OTP_REQUEST, verifyOTPSaga);
}

function* watchCompleteProfile() {
    yield takeEvery(type.COMPLETE_PROFILE_REQUEST, completeProfileSaga);
}

function* watchSendEmailOTP() {
    yield takeEvery(type.SEND_EMAIL_OTP_REQUEST, sendEmailOTPSaga);
}

function* watchVerifyEmailOTP() {
    yield takeEvery(type.VERIFY_EMAIL_OTP_REQUEST, verifyEmailOTPSaga);
}

export default function* userSaga() {
    yield all([
        fork(watchAddUser),
        fork(watchLogin),
        fork(watchUserDetails),
        fork(watchUserDetailsFromToken),
        fork(watchGetUserActivity),
        fork(watchSetUserActivity),
        fork(watchGetUserPreference),
        fork(watchSetUserPreference),
        fork(watchSetFeedback),
        fork(watchSendOTP),
        fork(watchVerifyOTP),
        fork(watchCompleteProfile),
        fork(watchSendEmailOTP),
        fork(watchVerifyEmailOTP),
    ]);
}